import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, MapPin, Users, ArrowLeft, RefreshCw, FileText } from 'lucide-react';
import { format, addDays, getDay } from 'date-fns';
import { pl } from 'date-fns/locale';
import { API_BASE_URL } from '../config/environment';

const getNextOccurrence = (recurringDays) => {
  if (!Array.isArray(recurringDays) || recurringDays.length === 0) return new Date();
  const today = new Date();
  const todayDay = getDay(today);
  let minDays = 8;
  for (const day of recurringDays) {
    let diff = day - todayDay;
    if (diff <= 0) diff += 7;
    if (diff < minDays) minDays = diff;
  }
  return addDays(today, minDays);
};

const DAY_NAMES = {
  pl: ['niedzielę', 'poniedziałek', 'wtorek', 'środę', 'czwartek', 'piątek', 'sobotę'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
};

const formatRecurringDays = (days, lang) => {
  if (!Array.isArray(days) || days.length === 0) return '';
  const names = DAY_NAMES[lang] || DAY_NAMES['pl'];
  const sorted = [...days].sort((a, b) => a - b);
  return sorted.map(d => names[d]).join(', ');
};

const EventDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events/slug/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('notFound');
          } else {
            throw new Error('Błąd podczas pobierania wydarzenia');
          }
          return;
        }
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        setError('fetchError');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="flex space-x-2">
          <span className="w-3 h-3 bg-cream rounded-full animate-bounce"></span>
          <span className="w-3 h-3 bg-cream rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
          <span className="w-3 h-3 bg-cream rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
        </div>
      </div>
    );
  }

  if (error === 'notFound') {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-center space-y-6 px-4">
          <h1 className="text-4xl font-bold text-cream">{t('events.details.notFound', 'Wydarzenie nie zostało znalezione')}</h1>
          <p className="text-cream/70 text-lg">{t('events.details.notFoundDesc', 'To wydarzenie nie istnieje lub zostało usunięte.')}</p>
          <button
            onClick={() => navigate('/events')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-secondary font-semibold rounded-lg
              hover:bg-primaryLight transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('events.backToList', 'Powrót do listy wydarzeń')}
          </button>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-xl text-primary bg-secondaryLight px-6 py-4 rounded-lg shadow-lg">
          {t('events.fetchError', 'Błąd podczas ładowania wydarzenia')}
        </div>
      </div>
    );
  }

  const lang = i18n.language?.substring(0, 2) || 'pl';
  const isRecurring = event.isRecurring === true || event.isRecurring === 'true';
  const recurringDays = isRecurring ? (event.recurringDays || (event.recurringDay !== undefined ? [event.recurringDay] : [])) : [];
  const eventDate = isRecurring ? getNextOccurrence(recurringDays) : (event.date ? new Date(event.date) : null);
  const hasTime = event.timeFrom || event.time;
  const timeDisplay = event.timeFrom
    ? (event.timeTo ? `${event.timeFrom} – ${event.timeTo}` : event.timeFrom)
    : event.time || null;
  const recurringDaysText = isRecurring ? formatRecurringDays(recurringDays, lang) : '';

  return (
    <div className="min-h-screen bg-secondary">
      {/* Hero Section */}
      <div className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
        {event.image ? (
          <img
            src={`${API_BASE_URL}${event.image}`}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-secondaryLight to-secondary" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/60 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            {isRecurring && (
              <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 bg-primary/20 border border-primary/40
                rounded-full text-primary text-sm font-medium backdrop-blur-sm">
                <RefreshCw className="w-4 h-4" />
                {recurringDaysText}
              </span>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-cream leading-tight">
              {event.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        {/* Back button */}
        <button
          onClick={() => navigate('/events')}
          className="inline-flex items-center gap-2 text-cream/70 hover:text-cream transition-colors
            duration-200 mb-8 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
          {t('events.backToList', 'Powrót do listy wydarzeń')}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-secondaryLight/50 rounded-2xl p-6 md:p-8 border border-cream/10">
              <h2 className="text-xl font-bold text-cream mb-4">{t('events.details.description', 'Opis')}</h2>
              <p className="text-cream/80 leading-relaxed whitespace-pre-line">{event.description}</p>
            </div>

            {/* Regulations */}
            {event.regulations && event.regulations.trim() !== '' && event.regulations !== '<p><br></p>' && (
              <div className="bg-secondaryLight/50 rounded-2xl p-6 md:p-8 border border-primary/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold text-cream">{t('events.regulations', 'Regulamin')}</h2>
                </div>
                <div
                  className="max-w-none text-cream/80
                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-cream [&_h1]:mb-4 [&_h1]:mt-6
                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-cream [&_h2]:mb-3 [&_h2]:mt-5
                    [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-cream [&_h3]:mb-2 [&_h3]:mt-4
                    [&_p]:mb-3 [&_p]:leading-relaxed
                    [&_strong]:text-cream [&_strong]:font-bold
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                    [&_li]:mb-1
                    [&_a]:text-primary [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: event.regulations }}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event info card */}
            <div className="bg-secondaryLight/50 rounded-2xl p-6 border border-cream/10 space-y-5 sticky top-24">
              <h3 className="text-lg font-bold text-cream">{t('events.details.info', 'Informacje')}</h3>

              <div className="space-y-4">
                {/* Next date — only show if there's an actual date */}
                {eventDate && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-cream/50 text-sm">
                        {isRecurring ? t('events.nextDate', 'Najbliższy termin') : t('events.date', 'Data')}
                      </p>
                      <p className="text-cream font-medium">
                        {format(eventDate, 'd MMMM yyyy', { locale: pl })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Time — only show if provided */}
                {timeDisplay && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-cream/50 text-sm">{t('events.time', 'Godzina')}</p>
                      <p className="text-cream font-medium">{timeDisplay}</p>
                    </div>
                  </div>
                )}

                {/* Location — only show if provided */}
                {event.location && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-cream/50 text-sm">{t('events.location', 'Lokalizacja')}</p>
                      <p className="text-cream font-medium">{event.location}</p>
                    </div>
                  </div>
                )}

                {/* Participants — only show if provided */}
                {event.maxParticipants && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-cream/50 text-sm">{t('events.availableSlots', 'Dostępne miejsca')}</p>
                      <p className="text-cream font-medium">
                        {event.maxParticipants - (event.participants?.length || 0)}/{event.maxParticipants}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Price */}
              {event.price && (
                <div className="pt-4 border-t border-cream/10">
                  <div className="flex items-center justify-between">
                    <span className="text-cream/50">{t('events.price', 'Cena')}</span>
                    <span className="text-2xl font-bold text-primary">{event.price} zł</span>
                  </div>
                </div>
              )}

              {/* Recurring badge in sidebar */}
              {isRecurring && (
                <div className="pt-4 border-t border-cream/10">
                  <div className="flex items-center gap-2 text-primary/80 text-sm">
                    <RefreshCw className="w-4 h-4" />
                    <span>{t('events.recurringInfo', 'To wydarzenie odbywa się co tydzień')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsPage;
